import { useCallback, useState } from 'react';
import { PlanAddonTypes } from '../../modules/renew-plan/v1/typings';
import useNotifications from './use-notifications';
import { openPopup } from '../utils/popup';
import { getPaymentPageUrl } from '../requests/get-payment-page-url';

export interface UseAddonPurchaseOptions {
  /** Called after the payment page is opened successfully (e.g. close modal) */
  onSuccess?: () => void;
}

export interface UseAddonPurchaseResult {
  loading: boolean;
  /** If addonId was passed to the hook, can be called with no args. Otherwise pass addonId. */
  purchaseAddon: (addonId?: PlanAddonTypes) => Promise<void>;
}

/**
 * Hook to redirect user to buy an addon (payment page). Handles loading and error notifications.
 * @param addonId - Optional. When provided, purchaseAddon() can be called with no args (e.g. onClick={purchaseAddon}).
 * @param options.onSuccess - Optional. Called after payment URL is opened (e.g. close modal).
 */
export function useAddonPurchase(addonId?: PlanAddonTypes, options?: UseAddonPurchaseOptions): UseAddonPurchaseResult {
  const [loading, setLoading] = useState(false);
  const { addNotification } = useNotifications();
  const onSuccess = options?.onSuccess;

  const purchaseAddon = useCallback(
    async (overrideAddonId?: PlanAddonTypes): Promise<void> => {
      const id = overrideAddonId ?? addonId;
      setLoading(true);
      try {
        const response = await getPaymentPageUrl(id);
        setLoading(false);
        if (response?.paymentPageUrl) {
          openPopup(response.paymentPageUrl);
          onSuccess?.();
        } else {
          throw new Error('Invalid payment URL received');
        }
      } catch (err) {
        setLoading(false);
        const errorMessage = err instanceof Error ? err.message : 'Failed to get payment URL';
        addNotification({
          message: errorMessage,
          type: 'error',
        });
      }
    },
    [addonId, onSuccess, addNotification]
  );

  return { loading, purchaseAddon };
}

export default useAddonPurchase;
